import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@apollo/client";
import { DateValueType } from "react-tailwindcss-datepicker";
import { SOME_USERS_QUERY } from "@/pages/people/queries";
import { dateValueTypeToISOString } from "@/features/global/components/NotesTable/noteHelpers";
import { DEPARTMENTS_QUERY } from "@/features/global/components/AddTaskSideSheet/queries";
import {
  addWorkdayTeamTag,
  buildTeamVisibilityOptions
} from "@/features/global/components/AddTaskSideSheet/teamVisibilityOptions";
import { useFlags } from "@/hooks/useFeatureFlags";
import { TaskVisibilityValues, type TaskVisibility } from "@/types";

export type BackofficeCRMNestedTaskInput = {
  name: string;
  description: string;
  assignedToId?: string | null;
  dueDate?: string | null;
  stage?: string;
  visibility?: TaskVisibility;
  teamTags?: string[];
};

export type BackofficeUser = {
  id: string;
  name: string;
  email: string;
  department?: string | null;
};

export type AttachTaskErrors = {
  taskName?: string;
  taskAssignee?: string;
};

export type AttachTaskCurrentUser = {
  id: string;
  name: string;
  email: string;
  department?: string | null;
};

function defaultDueDate(): DateValueType {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return { startDate: date, endDate: date };
}

export default function useAttachTask(currentUser?: AttachTaskCurrentUser | null) {
  const flags = useFlags();
  const teamsEnabled = !!flags["m360-department-task-queue"];
  const wasTeamsEnabledRef = useRef(teamsEnabled);

  const [attachTask, setAttachTask] = useState(false);
  const [taskName, setTaskName] = useState("");
  const [taskAssignee, setTaskAssignee] = useState<BackofficeUser | null>(null);
  const [taskAssigneeSearch, setTaskAssigneeSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [taskDueDate, setTaskDueDate] = useState<DateValueType>(null);
  const [taskIsPrivate, setTaskIsPrivate] = useState(false);
  const [taskErrors, setTaskErrors] = useState<AttachTaskErrors>({});
  const [selectedTeamTags, setSelectedTeamTags] = useState<string[]>([]);
  const [teamSearchTerm, setTeamSearchTerm] = useState("");

  const { data: usersData, loading: usersLoading } = useQuery(SOME_USERS_QUERY, {
    variables: { search: debouncedSearch },
    skip: !debouncedSearch
  });

  const { data: departmentsData } = useQuery(DEPARTMENTS_QUERY, {
    skip: !teamsEnabled || !attachTask
  });

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(taskAssigneeSearch), 300);
    return () => clearTimeout(handler);
  }, [taskAssigneeSearch]);

  // Flags start empty until Flagr hydrates. If Attach Task was enabled while
  // teamsEnabled was still false, re-apply the current assignee/user department
  // when the department-queue flag flips on (without undoing later chip removals).
  useEffect(() => {
    const justEnabled = teamsEnabled && !wasTeamsEnabledRef.current;
    wasTeamsEnabledRef.current = teamsEnabled;

    if (!attachTask || !justEnabled) return;
    const department = taskAssignee?.department ?? currentUser?.department;
    if (!department) return;
    setSelectedTeamTags((prev) => addWorkdayTeamTag(prev, department));
  }, [attachTask, teamsEnabled, taskAssignee?.department, currentUser?.department]);

  const initialize = useCallback(
    (defaultName: string) => {
      setTaskName(defaultName);
      setTaskDueDate(defaultDueDate());
      setTaskIsPrivate(false);
      setTaskErrors({});
      setTeamSearchTerm("");
      if (currentUser) {
        setTaskAssignee({
          id: currentUser.id,
          name: currentUser.name,
          email: currentUser.email,
          department: currentUser.department
        });
        setTaskAssigneeSearch(currentUser.name);
        setSelectedTeamTags(teamsEnabled ? addWorkdayTeamTag([], currentUser.department) : []);
      }
    },
    [currentUser, teamsEnabled]
  );

  const reset = useCallback(() => {
    setAttachTask(false);
    setTaskName("");
    setTaskAssignee(null);
    setTaskAssigneeSearch("");
    setDebouncedSearch("");
    setTaskDueDate(null);
    setTaskIsPrivate(false);
    setTaskErrors({});
    setSelectedTeamTags([]);
    setTeamSearchTerm("");
  }, []);

  const validate = useCallback((): boolean => {
    const errors: AttachTaskErrors = {};
    if (!teamsEnabled && !taskAssignee) errors.taskAssignee = "Assignee is required";
    setTaskErrors(errors);
    return Object.keys(errors).length === 0;
  }, [taskAssignee, teamsEnabled]);

  const getTaskInput = useCallback(
    (defaultName?: string): BackofficeCRMNestedTaskInput | null => {
      if (!attachTask) return null;
      if (!teamsEnabled && !taskAssignee) return null;
      const name = taskName.trim() || defaultName?.trim() || "";
      const input: BackofficeCRMNestedTaskInput = {
        name,
        description: "",
        visibility: taskIsPrivate ? TaskVisibilityValues.PRIVATE : TaskVisibilityValues.PUBLIC
      };
      if (taskAssignee) {
        input.assignedToId = taskAssignee.id;
      }
      const dueDateValue = dateValueTypeToISOString(taskDueDate);
      if (dueDateValue) input.dueDate = dueDateValue;
      if (teamsEnabled && selectedTeamTags.length > 0) {
        input.teamTags = selectedTeamTags;
      }
      return input;
    },
    [attachTask, taskName, taskAssignee, taskDueDate, taskIsPrivate, teamsEnabled, selectedTeamTags]
  );

  const toggleAttachTask = useCallback(
    (enabled: boolean, defaultName: string) => {
      if (enabled) {
        setAttachTask(true);
        initialize(defaultName);
      } else {
        reset();
      }
    },
    [initialize, reset]
  );

  const selectAssignee = useCallback(
    (user: BackofficeUser) => {
      setTaskAssignee(user);
      setTaskAssigneeSearch(user.name);
      if (teamsEnabled) {
        setSelectedTeamTags((prev) => addWorkdayTeamTag(prev, user.department));
      }
    },
    [teamsEnabled]
  );

  const clearAssignee = useCallback(() => {
    setTaskAssignee(null);
    setTaskAssigneeSearch("");
  }, []);

  const clearError = useCallback((field: keyof AttachTaskErrors) => {
    setTaskErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const availableDepartmentOptions = buildTeamVisibilityOptions(
    departmentsData?.departments || [],
    selectedTeamTags,
    teamSearchTerm
  );

  const handleTeamVisibilitySelect = useCallback((option: { value: string; label: string }) => {
    setSelectedTeamTags((prev) => addWorkdayTeamTag(prev, option.value));
    setTeamSearchTerm("");
  }, []);

  const removeTeamTag = useCallback((department: string) => {
    setSelectedTeamTags((prev) => prev.filter((tag) => tag !== department));
  }, []);

  return {
    attachTask,
    toggleAttachTask,
    taskName,
    setTaskName,
    taskAssignee,
    selectAssignee,
    clearAssignee,
    taskAssigneeSearch,
    setTaskAssigneeSearch,
    taskDueDate,
    setTaskDueDate,
    taskIsPrivate,
    setTaskIsPrivate,
    taskErrors,
    clearError,
    usersData: usersData?.backofficeUsers || [],
    usersLoading,
    validate,
    getTaskInput,
    reset,
    teamsEnabled,
    selectedTeamTags,
    teamSearchTerm,
    setTeamSearchTerm,
    availableDepartmentOptions,
    handleTeamVisibilitySelect,
    removeTeamTag
  };
}
